import { CropManagementOption, CropStyle, ICrop, IVariety, setupCropsAndVarieties } from "./crops";
import { CroppingStyleLookup } from "./fields/styles";
import { CropStyleFillPattern, RegionId } from "./generated/enums";
import { filterAvailableTypes, setupManureTypesForRegion } from "./manure";
import { IFertiliserProduct, IManureType } from "./models/omnia";
import { omniaResolve } from "./promises";
import { ISoilType, ISubsoilType, SsmClassification } from "./soil";
import { IOmniaHttpService, IOmniaSession } from "./types";
import { isArrayEmpty } from "./utils/array";

interface ApiSharedData {
    SoilTypes: ISoilType[];
    SubsoilTypes: ISubsoilType[];

    Crops: ICrop[];
    Varieties: IVariety[];

    CropStyles: CropStyle[];

    CropManagementOptions: CropManagementOption[];

    FertiliserProducts: IFertiliserProduct[];

    ManureTypes: IManureType[];

    Soil: {
        SsmClassification: SsmClassification[];
    };
}

export interface SoilSharedData {
    ssmClassification: SsmClassification[];
}

export interface SharedDataService {
    refresh(auto?: boolean): ng.IPromise<any>;

    readonly soilTypes: ISoilType[];
    readonly subsoilTypes: ISubsoilType[];

    readonly crops: ICrop[];
    readonly varieties: IVariety[];
    readonly cropStyles: CropStyle[];

    readonly fertiliserProducts: IFertiliserProduct[];
    getManureTypes(region: RegionId): IManureType[];
    getManureTypesForRegion(region: RegionId, retainBlends?: boolean): IManureType[];

    readonly soil: SoilSharedData;

    getCropManagementOptions(region: RegionId): CropManagementOption[];
}

const source = "shared";

/** @internal */
export class SharedDataServiceImpl implements SharedDataService {
    static $inject = ["OmniaSession", "OmniaHttpService"];

    private _holdingId: number;
    private _year: number;
    private _version = 0;

    private _refresh: ng.IPromise<any>;

    private _soilTypes: ISoilType[] = [];
    private _subsoilTypes: ISubsoilType[] = [];

    private _crops: ICrop[] = [];
    private _varieties: IVariety[] = [];
    private _cropStyles: CropStyle[];
    private _cropManagementOptions: CropManagementOption[] = [];

    private _fertiliserProducts: IFertiliserProduct[] = [];
    private _manureTypes: IManureType[] = [];

    private _soil: SoilSharedData = {
        ssmClassification: [],
    };

    constructor(
        private readonly session: IOmniaSession,
        private readonly http: IOmniaHttpService,
        private readonly apiState = session.apiState
    ) {}

    get soilTypes() {
        return this._soilTypes;
    }

    get subsoilTypes() {
        return this._subsoilTypes;
    }

    get crops() {
        return [...this._crops];
    }

    get cropStyles() {
        return this._cropStyles;
    }

    get varieties() {
        return [...this._varieties];
    }

    getCropManagementOptions(region: RegionId) {
        return this._cropManagementOptions.filter((o) => o.Region === region);
    }

    get fertiliserProducts() {
        return [...this._fertiliserProducts];
    }

    getManureTypes(region: RegionId) {
        return setupManureTypesForRegion(this._manureTypes, region);
    }

    getManureTypesForRegion(region: RegionId, retainBlends?: boolean) {
        return filterAvailableTypes(this.getManureTypes(region), region, retainBlends);
    }

    get soil() {
        return this._soil;
    }

    refresh(auto: boolean = true) {
        if (this._refresh) {
            return this._refresh;
        }

        const { holdingId, yearId } = this.session;

        let version = this._version;

        if (auto && holdingId == this._holdingId && yearId == this._year) {
            const changes = this.apiState.getChangesFromSource(this._version, (s) => s == source || s == "refresh-all");

            if (isArrayEmpty(changes)) {
                return omniaResolve();
            }

            version = changes[changes.length - 1].id;
        }

        this._soil = undefined;

        const url = `/api/shared-data/v2/?holdingId=${holdingId}&year=${yearId}`;

        return (this._refresh = this.http.get(url).then((r) => {
            const data = r.data as ApiSharedData;

            setupCropsAndVarieties(data.Crops, data.Varieties);

            this._soilTypes = data.SoilTypes;
            this._subsoilTypes = data.SubsoilTypes;

            this._cropStyles = data.CropStyles;
            const croppingStyleLookup = new CroppingStyleLookup(this._cropStyles);

            this._crops = setupCrops(data.Crops, croppingStyleLookup, yearId);
            this._varieties = setupVarieties(data.Varieties, croppingStyleLookup, yearId);
            this._cropManagementOptions = data.CropManagementOptions ?? [];

            this._fertiliserProducts = data.FertiliserProducts;
            this._manureTypes = data.ManureTypes;

            this._holdingId = holdingId;
            this._year = yearId;

            this._soil = {
                ssmClassification: setupSsmClassification(data.Soil?.SsmClassification || []),
            };

            this._version = version;
            this._refresh = undefined;

            if (this.session.debugMode) {
                console.log(`%cSharedData.refresh`, "font-weight: bold");
            }
        }));
    }
}

function setupSsmClassification(classification: SsmClassification[]) {
    return (classification || []).map((c) => ({
        ...c,
        MinValue: c.MinValue || 0,
    }));
}

function setupCrops(crops: ICrop[], styleLookup: CroppingStyleLookup, year: number) {
    return (crops || []).map((c) => {
        if (c.CropId) {
            const style = styleLookup.findForCrop(year, c.CropId);
            return updateStyledObject(c, style);
        }
        return c;
    });
}

function setupVarieties(varieties: IVariety[], styleLookup: CroppingStyleLookup, year: number) {
    return (varieties || []).map((v) => {
        if (v.CropVarietyId) {
            const style = styleLookup.findForVariety(year, v.CropId, v.CropVarietyId);
            return updateStyledObject(v, style);
        }
        return v;
    });
}

/**
 * Updates a "styled object" (currently, either a crop or a variety) to replace its default
 * style by a user-provided one.
 * If the default style is replaced, it is still available through the DefaultStyle property
 *
 * @param obj Target
 * @param style Style to set on the target
 * @returns Updated target
 */
function updateStyledObject<T extends { Style?: ICrop["Style"] }>(obj: T, style: CropStyle) {
    return !style?.Color
        ? obj
        : {
              ...obj,
              DefaultStyle: obj.Style,
              Style: {
                  Colour: style.Color,
                  Pattern: style.Pattern || CropStyleFillPattern.None,
              },
          };
}
